import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import { FileItem } from '../types';
import { websocketService, FileStatusUpdate } from './websocketService';

// --- 类型定义保持不变 ---
export interface FileUploadRequest {
    file: File;
    originalName: string;
    mimeType: string;
    chatSession: string;
}

export interface FileUploadResponse {
    id: string;
    original_name: string;
    mime_type: string;
    size: number;
    created_at: string;
}

export interface FileStatusResponse {
    status: 'pending' | 'processing' | 'to_be_confirmed' | 'needs_review' | 'ntrk_analysis' | 'completed' | 'ntrk_analysis_completed' | 'failed' | 'uploaded' | 'attached' | 'cancelling' | 'cancelled';
    status_message?: string;
    errors?: string;
    variants?: any[];
    // 服务端自动确认模式标志：true 时 to_be_confirmed 只是过渡态，后台自动继续分析
    auto_confirm?: boolean;
}
const STAGE_ONE_FINAL_STATES = ['to_be_confirmed', 'failed', 'completed', 'ntrk_analysis_completed', 'needs_review', 'cancelled', 'cancelling'];
type StatusCallback = (update: FileStatusUpdate) => void;
class FileService {

    private statusListeners: Set<StatusCallback> = new Set();

    constructor() {
        // ✅ 全局常驻广播订阅：所有 file_status_update 都转发给 UI 监听器。
        // 关键点：不能依附在 waitForFilesProcessing 的生命周期上——
        // 用户确认卡片可能发生在等待结束后很久（Phase 2 的 ntrk_analysis_completed
        // 也需要送达），因此广播通道必须是常驻的。
        websocketService.subscribe('file_status_update', (update: FileStatusUpdate) => {
            this.broadcastStatus(update);
        });
    }

    // 内部方法：每当状态更新时调用（无论来自 WS 还是轮询）
    private broadcastStatus(update: FileStatusUpdate) {
    // 使用 try-catch 避免某个 UI 监听器报错导致整个 Service 崩溃
        this.statusListeners.forEach(callback => {
            try {
                callback(update);
            } catch (e) {
                console.error('UI Listener Error:', e);
            }
        });
    }
    

    // 供组件订阅的方法
    public onStatusUpdate(callback: StatusCallback) {
        this.statusListeners.add(callback);
        return () => {this.statusListeners.delete(callback);};
    }
    // 用于管理后备轮询的计时器
    private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();
    private pollingTimeouts: Map<string, NodeJS.Timeout> = new Map();

    /**
     * 上传文件逻辑 (保持原样)
     */
    async uploadFile(request: FileUploadRequest, authString: string): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append('file', request.file);
        formData.append('original_name', request.originalName);
        formData.append('mime_type', request.mimeType);
        formData.append('chat_session', request.chatSession);

        const response = await apiClient.uploadFile<FileUploadResponse>(
            API_ENDPOINTS.FILES.UPLOAD,
            formData,
            authString
        );

        if (response.error) throw new Error(response.error);
        return response.data!;
    }

    async uploadMultipleFiles(files: FileItem[], sessionId: string, authString: string): Promise<string[]> {
        // 2026-09-17 多文件合并上传（与研发机生产链对齐：一次案例多图合并跑全链，
        // 不按页拆案）。batch 端点只给主文件起分析任务，附件随主文件一起进链；
        // 返回需跟踪进度的文件 id 列表 = 仅主文件（附件行 status='attached' 不流转）。
        if (files.length === 1 && !files[0].name.toLowerCase().endsWith('.zip')) {
            const res = await this.uploadFile({
                file: files[0].file,
                originalName: files[0].name,
                mimeType: files[0].type || 'application/octet-stream',
                chatSession: sessionId,
            }, authString);
            return [res.id];
        }
        // zip 单件同样走 batch：后端 _expand_zip_files 展开为合并案例（友好性⑨）
        const formData = new FormData();
        files.forEach(f => formData.append('files', f.file, f.name));
        formData.append('chat_session', sessionId);
        const response = await apiClient.uploadFile<{ id: string; attached_count: number }>(
            API_ENDPOINTS.FILES.BATCH_UPLOAD,
            formData,
            authString
        );
        if (response.error) throw new Error(response.error);
        return [response.data!.id];
    }

    /** 取消分析（友好性③：协作式，子进程轮询点生效） */
    async cancelFile(fileId: string, authString: string): Promise<void> {
        const response = await apiClient.post<{ ok: boolean }>(
            `/uploaded-files/${fileId}/cancel/`, {}, authString);
        if (response.error) throw new Error(response.error);
    }

    /** 会话文件列表（友好性④刷新恢复数据源；completed 带 structured_summary 供历史回填⑤⑥⑦） */
    async getSessionFiles(sessionId: string, authString: string): Promise<{ id: string; file_name: string; status: string; status_message: string; structured_summary?: any }[]> {
        const response = await apiClient.get<{ id: string; file_name: string; status: string; status_message: string; structured_summary?: any }[]>(
            `/uploaded-files/by-session/?session_id=${sessionId}`, authString);
        if (response.error) throw new Error(response.error);
        return response.data || [];
    }

    /** 留痕文件（友好性⑦：中间文件折叠块） */
    async getArtifacts(fileId: string, authString: string): Promise<{ title: string; file: string; content: string }[]> {
        const response = await apiClient.get<{ artifacts: { title: string; file: string; content: string }[] }>(
            `/uploaded-files/${fileId}/artifacts/`, authString);
        if (response.error) return [];
        return response.data?.artifacts || [];
    }

    /**
     * 获取文件状态 (HTTP 接口)
     */
    async getFileStatus(fileId: string, authString: string): Promise<FileStatusResponse> {
        const response = await apiClient.get<FileStatusResponse>(
            API_ENDPOINTS.FILES.STATUS(fileId),
            authString
        );
        if (response.error) throw new Error(response.error);
        return response.data!;
    }

    /**
     * 手动录入变异信息：创建无附件的虚拟文件（直接进入待确认状态），
     * 返回 file_id 供前端挂载确认卡片
     */
    async createManualEntry(chatSessionId: string, authString: string, note?: string): Promise<{ file_id: string; file_name: string; status: string }> {
        const response = await apiClient.post<{ file_id: string; file_name: string; status: string }>(
            API_ENDPOINTS.FILES.MANUAL_ENTRY,
            { chat_session: chatSessionId, note: note || '' },
            authString
        );
        if (response.error) throw new Error(response.error);
        return response.data!;
    }

    /**
     * 核心逻辑：等待文件处理完成 (WebSocket + 轮询双保险)
     */


    async waitForFilesProcessing(
        fileIds: string[],
        authString: string,
        onProgress?: (message: string, update?: FileStatusUpdate) => void, // update 供前端构造带状态/详情的步骤条目
        sessionId?: string,  // 新增：会话ID，用于 WebSocket 订阅
        timeoutMinutes = 3
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const fileStatuses = new Map<string, string>();
            const unsubscribers: (() => void)[] = [];
            
            const globalTimeoutId = setTimeout(() => {
                cleanup();
                reject(new Error('文件处理超时'));
            }, timeoutMinutes * 60 * 1000);

            const cleanup = () => {
                unsubscribers.forEach(unsub => unsub());
                fileIds.forEach(id => this.stopPolling(id));
                clearTimeout(globalTimeoutId);
            };

            const checkAllReadyForConfirmation = () => {
                const statuses = Array.from(fileStatuses.values());

                // 只要所有文件都进入了"第一阶段终态"（成功、失败或待确认）
                if (statuses.length === fileIds.length &&
                    statuses.every(s => STAGE_ONE_FINAL_STATES.includes(s))) {

                    // ✅ 等待结束后立即清理本次等待专属的订阅与计时器：
                    // - 全局超时计时器若在用户核对期间触发，会误杀订阅通道
                    // - 后续 Phase 2 的状态推送由构造函数的常驻广播订阅承接
                    cleanup();

                    // 取消优先（0918 实测：cancelling 在终态表里，被误判成"解析完成"）
                    const hasCancelled = statuses.some(s => s === 'cancelled' || s === 'cancelling');
                    const hasFailed = statuses.some(s => s === 'failed');
                    onProgress?.(hasCancelled ? '⏹ 已取消本次分析。'
                                 : hasFailed ? '解析部分完成，请手动补全失败数据' : '解析完成，请核对数据');

                    // 重点：这里 resolve 代表"第一阶段阻塞结束"，进入人工环节
                    resolve();
                }
            };

            fileIds.forEach(fileId => {
                fileStatuses.set(fileId, 'processing');

                // WebSocket 监听（仅用于本次等待的就绪判定与进度提示；
                // 广播由全局常驻订阅负责，这里不再重复 broadcast）
                const unsub = websocketService.subscribe('file_status_update', (update: FileStatusUpdate) => {
                    if (update.file_id !== fileId) return;

                    fileStatuses.set(fileId, update.status);
                    onProgress?.(update.status_message || update.status, update);
                    checkAllReadyForConfirmation();
                });
                unsubscribers.push(unsub);

                // 发送订阅指令并开启快速轮询补偿（30秒后启动）
                // 关键修复：传递 session_id 给 WebSocket
                websocketService.send({ 
                    type: 'subscribe_file', 
                    file_id: fileId,
                    session_id: sessionId  // ✅ 新增：传递 session_id
                });
                this.startFallbackPolling(fileId, authString, fileStatuses, onProgress, checkAllReadyForConfirmation, 3*60*1000);
            });
        });
    }

    /**
     * 后备轮询逻辑
     */

    private startFallbackPolling(
        fileId: string,
        authString: string,
        fileStatuses: Map<string, string>,
        onProgress?: (msg: string) => void,
        onCheck?: () => void,
        delayMs = 180000 // <--- 添加这个参数，默认值为 3 分钟
    ) {
        // 将原本硬编码的 180000 改为 delayMs
        const timeoutId = setTimeout(() => {
            const currentStatus = fileStatuses.get(fileId);
            if (!STAGE_ONE_FINAL_STATES.includes(currentStatus || '')) {
                console.warn(`⏰ 文件 ${fileId} 响应慢，启动 HTTP 轮询`);
                
                const intervalId = setInterval(async () => {
                    try {
                        const res = await this.getFileStatus(fileId, authString);
                        this.broadcastStatus({
                            type: 'file_status_update',
                            file_id: fileId,
                            status: res.status,
                            status_message: res.status_message,
                            progress: res.status === 'completed' ? 100 : undefined 
                        });
                        fileStatuses.set(fileId, res.status);
                        if (res.status_message) onProgress?.(res.status_message);
                        onCheck?.();

                        if (res.status === 'completed' || res.status === 'ntrk_analysis_completed' || res.status === 'failed') {
                            this.stopPolling(fileId);
                        }
                    } catch (e) {
                        console.error('轮询出错:', e);
                    }
                }, 15000); // 轮询间隔通常固定即可

                this.pollingIntervals.set(fileId, intervalId);
            }
        }, delayMs); // <--- 使用传入的延迟

        this.pollingTimeouts.set(fileId, timeoutId);
    }
    private stopPolling(fileId: string) {
        if (this.pollingIntervals.has(fileId)) {
            clearInterval(this.pollingIntervals.get(fileId)!);
            this.pollingIntervals.delete(fileId);
        }
        if (this.pollingTimeouts.has(fileId)) {
            clearTimeout(this.pollingTimeouts.get(fileId)!);
            this.pollingTimeouts.delete(fileId);
        }
    }
}

export const fileService = new FileService();